// The history module (#3382 milestone 1): what an entry of a conversation's view holds of the
// committed rows (seqs, ranges, text chunks and bodies). Pure functions over entries.
import type { LedgerSide, StreamEntry, TextBody, TextChunk, TextPart } from "./types.js";

// The durable rows an entry covers: its parts' seqs and its own (stamped) seq.
export function seqsOf(e: StreamEntry): number[] {
  const out: number[] = [];
  if ((e.kind === "text" || e.kind === "thinking") && e.parts) {
    for (const part of e.parts) if (part.seq !== undefined) out.push(part.seq);
  }
  if (e.rows) out.push(...e.rows);
  if (e.seq !== undefined) out.push(e.seq);
  return out;
}

export function seqRangeOf(e: StreamEntry): { min: number; max: number } | undefined {
  const seqs = seqsOf(e);
  if (seqs.length === 0) return undefined;
  return { min: Math.min(...seqs), max: Math.max(...seqs) };
}

export function highestSeq(a: number | undefined, b: number | undefined): number | undefined {
  if (a === undefined) return b;
  if (b === undefined) return a;
  return Math.max(a, b);
}

// The rows an in-place entity has consumed so far, plus this one.
export function rowsWith(
  prev: StreamEntry | undefined,
  seq: number | undefined,
): readonly number[] {
  const had = prev?.rows ?? (prev?.seq === undefined ? [] : [prev.seq]);
  return seq === undefined || had.includes(seq) ? had : [...had, seq];
}

// Rows that follow each other are one turn: no divider between them (#3248 C2).
export function rowsFollowEachOther(
  a: StreamEntry | undefined,
  b: StreamEntry | undefined,
): boolean {
  const ra = a ? seqRangeOf(a) : undefined;
  const rb = b ? seqRangeOf(b) : undefined;
  return !!ra && !!rb && ra.max + 1 === rb.min;
}

// The parts a text entry already has — or, recorded before parts existed, one part for
// its whole content carrying its seq.
export function chunkPartsOf(e: TextBody): readonly TextPart[] {
  if (e.parts && e.parts.length > 0) return e.parts;
  return [{ ...(e.seq === undefined ? {} : { seq: e.seq }), len: e.content.length }];
}

// The chunks a text body is made of. A body recorded without them (a cached paint, an
// older build) is one chunk carrying the body's seq.
export function chunksOf(body: TextBody): TextChunk[] {
  if (!body.parts || body.parts.length === 0) {
    return [{ ...(body.seq === undefined ? {} : { seq: body.seq }), text: body.content }];
  }
  const out: TextChunk[] = [];
  let at = 0;
  for (const part of body.parts) {
    const text = body.content.slice(at, at + part.len);
    out.push({
      ...(part.seq === undefined ? {} : { seq: part.seq }),
      ...(part.ledger ? { ledger: part.ledger } : {}),
      text,
    });
    at += part.len;
  }
  if (at < body.content.length) out.push({ text: body.content.slice(at) });
  return out;
}

// THE normal form of a text body, whatever order its chunks arrived in: committed chunks in
// seq order, the live overhang after them unless committed rows spell it. Every path that
// builds a body goes through here (#3248 C1); the full rule: knowledge/124-sync/history-module.md.
export function bodyOfChunks(chunks: readonly TextChunk[]): TextBody {
  const bySeq = new Map<number, string>();
  const loose: Array<{ after: number; chunk: TextChunk }> = [];
  let after = -1;
  for (const c of chunks) {
    if (c.ledger === "head") continue;
    if (c.seq === undefined) {
      loose.push({ after, chunk: c });
      continue;
    }
    if (!bySeq.has(c.seq)) bySeq.set(c.seq, c.text);
    after = Math.max(after, c.seq);
  }
  const seqs = [...bySeq.keys()].sort((x, y) => x - y);
  const committed = seqs.map((seq) => ({ seq, text: bySeq.get(seq) ?? "" }));
  const kept = loose.filter((run) => {
    const since = committed
      .filter((c) => c.seq > run.after)
      .map((c) => c.text)
      .join("");
    return !since.endsWith(run.chunk.text);
  });
  const ordered: TextChunk[] = [
    ...ledgerChunk(chunks, "head"),
    ...committed,
    ...kept.map((run) => run.chunk),
  ];
  const parts: TextPart[] = [];
  for (const c of ordered) {
    parts.push({
      ...(c.seq === undefined ? {} : { seq: c.seq }),
      len: c.text.length,
      ...(c.ledger ? { ledger: c.ledger } : {}),
    });
  }
  return {
    content: ordered.map((c) => c.text).join(""),
    parts,
    ...(seqs.length === 0 ? {} : { seq: seqs[seqs.length - 1] }),
  };
}

// One side's ledger text, as the single chunk it normalises to (none when empty).
export function ledgerChunk(chunks: readonly TextChunk[], side: LedgerSide): TextChunk[] {
  const text = chunks
    .filter((c) => c.ledger === side)
    .map((c) => c.text)
    .join("");
  return text ? [{ text, ledger: side }] : [];
}

// A row next to the rows of a body that carries ledger text on that side (#3238). Where
// the ledger spells the row's text at that end, the row takes that text over; where it
// does not, the ledger was not this timeline's text there, so that side's ledger text is
// dropped and the row kept as it is. Nothing is ever covered without its text on screen.
// undefined when the row is not next to the rows, or that side has no ledger text.
export function takeRowIntoLedger(
  chunks: readonly TextChunk[],
  row: { seq: number; text: string },
): TextChunk[] | undefined {
  const seqs = chunks.flatMap((c) => (c.seq === undefined ? [] : [c.seq]));
  let side: LedgerSide | undefined;
  // A body that is ledger text only (a ledger row drawn before its timeline): its first row
  // takes over the start of that text.
  if (seqs.length === 0) side = chunks.every((c) => c.ledger === "tail") ? "tail" : undefined;
  else if (row.seq === Math.min(...seqs) - 1) side = "head";
  else if (row.seq === Math.max(...seqs) + 1) side = "tail";
  const ledger = side ? ledgerChunk(chunks, side)[0] : undefined;
  if (!side || !ledger) return undefined;
  // A row split finer than the ledger's text takes over only its own part of it.
  let left = "";
  if (side === "head" && ledger.text.endsWith(row.text)) {
    left = ledger.text.slice(0, ledger.text.length - row.text.length);
  } else if (side === "tail" && ledger.text.startsWith(row.text)) {
    left = ledger.text.slice(row.text.length);
  }
  // What is left stays where the ledger text stood (a tail among the live overhang).
  const at = chunks.findIndex((c) => c.ledger === side);
  const out: TextChunk[] = [];
  chunks.forEach((c, i) => {
    if (c.ledger !== side) out.push(c);
    else if (i === at && left) out.push({ text: left, ledger: side });
  });
  return [...out, row];
}

// A body drawn from a ledger row before its timeline (its message, whole) taking one of that
// message's rows: the rows spell the ledger text, so they take it over instead of repeating
// it. Its rowless chunks are that ledger text, or deltas that already took part of it over.
export function takeRowIntoLedgerBody(
  chunks: readonly TextChunk[],
  row: { seq: number; text: string },
): TextChunk[] | undefined {
  const held = chunks.map(
    (c): TextChunk => (c.seq === undefined ? { text: c.text, ledger: c.ledger ?? "tail" } : c),
  );
  return takeRowIntoLedger(held, row);
}

// The same for an uncommitted delta (no row seq yet): it takes over the ledger text up to
// where it spells it (a pane that joined mid-turn missed the opening deltas, so that opening
// stays as text). A ledger text it does not spell at all (rewritten on the server) gives way
// to the stream, this message's own text. undefined when no ledger text is left.
// `untouched`: the body is still the ledger row as drawn (no stream has reached it yet).
export function takeDeltaIntoLedgerBody(
  chunks: readonly TextChunk[],
  text: string,
  untouched: boolean,
): TextChunk[] | undefined {
  const held: readonly TextChunk[] = untouched
    ? [{ text: chunks.map((c) => c.text).join(""), ledger: "tail" }]
    : chunks;
  const tail = ledgerChunk(held, "tail")[0];
  if (!tail) return undefined;
  const kept = held.filter((c) => c.ledger !== "tail");
  // Only the first delta may start mid-text (a pane that joined mid-turn); later ones must
  // continue it, or a ledger text that differs from the stream would repeat.
  let at = tail.text.startsWith(text) ? 0 : -1;
  if (untouched) at = tail.text.indexOf(text);
  if (at < 0) return [...kept, { text }];
  const rest = tail.text.slice(at + text.length);
  return [
    ...kept,
    { text: tail.text.slice(0, at + text.length) },
    ...(rest ? [{ text: rest, ledger: "tail" as const }] : []),
  ];
}

export function withTextBody<E extends StreamEntry & { kind: "text" | "thinking" }>(
  e: E,
  body: TextBody,
): E {
  const seqs = [e.seq, body.seq].flatMap((n) => (n === undefined ? [] : [n]));
  return {
    ...e,
    content: body.content,
    parts: body.parts ?? [],
    ...(seqs.length === 0 ? {} : { seq: Math.max(...seqs) }),
  };
}

// A sub-agent card that exists only because a PROGRESS row reached the tab (a background call
// started in an earlier turn, or before the tab opened): no other row of it was ever seen.
// It does not end the reply being spoken. A card the tab saw launched (a foreground Task inside
// one API message) is a real boundary and still splits (C2).
export function cardIsProgressOnly(e: StreamEntry | undefined): boolean {
  return e?.kind === "tool_call" && e.detail?.type === "sub_agent" && e.progressOnly === true;
}

// THE one rule for what two halves of one reply may have between them: a progress-only card or,
// for text that names its message, a row recorded mid-reply that is not part of it (an incoming
// message and its turn boundary, a notice, a task finish line). A REAL tool call splits (#4146 C2).
export function passableInReply(e: StreamEntry | undefined, named: boolean): boolean {
  if (cardIsProgressOnly(e)) return true;
  if (!named) return false;
  if (e?.kind === "tool_call") return e.name === "task_notification";
  return e?.kind === "user_message" || e?.kind === "turn_boundary" || e?.kind === "notice";
}

// The [prefix, suffix] indexes a seam's passable rows sit between: a named text's reach, else thinking's.
export function seamAround(
  prefix: readonly StreamEntry[],
  suffix: readonly StreamEntry[],
): [number, number] {
  const reach = (named: boolean): [number, number] => {
    let lo = prefix.length - 1;
    while (lo > 0 && passableInReply(prefix[lo], named)) lo -= 1;
    let hi = 0;
    while (hi < suffix.length && passableInReply(suffix[hi], named)) hi += 1;
    return [lo, hi];
  };
  const wide = reach(true);
  return prefix[wide[0]]?.kind === "text" ? wide : reach(false);
}
